import {
  CloseButtonProps,
  toast,
  ToastContainer,
  ToastContainerProps,
  ToastOptions,
} from 'react-toastify';
import IconButton from 'src/components/ui/IconButton';
import CloseIcon from 'src/assets/icons/close.svg';
import { ReactNode } from 'react';

import type { ApolloError } from '@apollo/client';

const contextClass = {
  surface: 'text-surface-alt-ghost-90 bg-surface-alt-ghost',
  primary: 'text-primary-ghost-90 bg-primary-ghost',
  secondary: 'text-secondary-ghost-90 bg-secondary-ghost',
  success: 'text-green-ghost-alt-90 bg-green-ghost',
  danger: 'text-red-ghost-alt-90 bg-red-ghost',
  warning: 'text-yellow-ghost-alt-90 bg-yellow-ghost',
  ai: 'text-ai-ghost-alt-90 bg-ai-ghost',
};

const typeToColor = (t: CloseButtonProps['type']) => {
  if (t === 'default') return 'primary';
  if (t === 'info') return 'secondary';
  if (t === 'error') return 'danger';

  return t;
};

export const ToastNotifications = (props: ToastContainerProps) => (
  <ToastContainer
    className="max-w-100 gap-3"
    toastClassName={(context) =>
      `${contextClass[typeToColor(context?.type ?? 'default')]}
        relative p-6 rounded-lg min-w-96 font-medium text-sm`
    }
    position="top-right"
    newestOnTop
    icon={false}
    hideProgressBar
    autoClose={3000}
    closeButton={CloseButton}
    {...props}
  />
);

const CloseButton = ({ closeToast, type }: CloseButtonProps) => (
  <IconButton
    size="small"
    aria-label="Dismiss notification"
    variant={typeToColor(type)}
    className="absolute right-1 top-1"
    Icon={CloseIcon}
    onPress={() => closeToast()}
  />
);

export const notify = (
  content: {
    title?: string;
    message?: string | ReactNode;
  },
  options?: ToastOptions
) =>
  toast(
    () => (
      <div className="flex flex-col gap-1">
        {Boolean(content?.title) && <strong>{content.title}</strong>}
        {Boolean(content?.message) && <p>{content.message}</p>}
      </div>
    ),
    options
  );

export const notifySupport = (title: string, message: string) =>
  notify(
    {
      title,
      message: (
        <>
          {message}
          <br />
          If you continue experiencing issues please contact support{' '}
          <a
            className="font-bold underline"
            href="mailto:support@ladpub.com"
            target="_top"
          >
            support@ladpub.com
          </a>
          .
        </>
      ),
    },
    { type: 'error', autoClose: false }
  );

export const graphQLErrorNotification = (apolloError: ApolloError) => {
  const networkError = apolloError?.networkError;
  const errors = apolloError?.graphQLErrors[0]?.extensions?.fields ?? {};
  const error = Object.values(errors).join(', ');
  // @ts-expect-error: the error could be recaptcha related
  const isReCaptchaError = Boolean(errors?.recaptcha);
  const message = apolloError?.message ?? 'Something went wrong!';

  if (networkError) {
    if (networkError.name === 'AbortError') {
      return null;
    }

    return notify(
      { title: 'Network Error!', message },
      { type: 'error', autoClose: false }
    );
  }

  if (isReCaptchaError) return notifySupport(message, error);

  if (error) {
    return notify({ title: message, message: error }, { type: 'error' });
  }

  return notify(
    { title: 'Something went wrong!', message },
    { type: 'error', autoClose: 8000 }
  );
};

export const dismissAllNotifications = () => toast.dismiss();
